import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const toneVariants = {
  default: 'text-charcoal-900',
  white: 'text-white',
  brand: 'text-brand-red',
  muted: 'text-charcoal-400',
  mutedLight: 'text-charcoal-300',
  subdued: 'text-charcoal-600',
  charcoal500: 'text-charcoal-500',
  charcoal700: 'text-charcoal-700',
  inherit: '',
} as const

const h1Variants = cva('font-bold tracking-tight', {
  variants: {
    tone: toneVariants,
    size: {
      page: 'text-4xl leading-[1.15] md:text-5xl lg:text-[3.5rem]',
      display: 'text-heading-xl',
      inherit: '',
    },
  },
  defaultVariants: { tone: 'default', size: 'page' },
})

export function TypographyH1({
  className,
  tone,
  size,
  ...props
}: React.ComponentProps<'h1'> & VariantProps<typeof h1Variants>) {
  return <h1 className={cn(h1Variants({ tone, size }), className)} {...props} />
}

const h2Variants = cva('font-bold tracking-tight', {
  variants: {
    tone: toneVariants,
    scale: {
      default: 'text-3xl',
      lg: 'text-3xl lg:text-4xl',
      xl: 'text-4xl',
      inherit: '',
    },
  },
  defaultVariants: { tone: 'default', scale: 'default' },
})

export function TypographyH2({
  className,
  tone,
  scale,
  ...props
}: React.ComponentProps<'h2'> & VariantProps<typeof h2Variants>) {
  return <h2 className={cn(h2Variants({ tone, scale }), className)} {...props} />
}

const h3Variants = cva('font-bold', {
  variants: {
    tone: toneVariants,
    scale: {
      inherit: '',
      sm: 'text-sm uppercase tracking-wide',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
    },
  },
  defaultVariants: { tone: 'default', scale: 'lg' },
})

export function TypographyH3({
  className,
  tone,
  scale,
  ...props
}: React.ComponentProps<'h3'> & VariantProps<typeof h3Variants>) {
  return <h3 className={cn(h3Variants({ tone, scale }), className)} {...props} />
}

const h4Variants = cva('font-bold', {
  variants: {
    tone: toneVariants,
    scale: {
      inherit: '',
      xs: 'text-xs uppercase tracking-wide',
      sm: 'text-sm uppercase tracking-wide',
      base: 'text-base uppercase tracking-wide',
      body: 'text-base',
    },
  },
  defaultVariants: { tone: 'default', scale: 'sm' },
})

export function TypographyH4({
  className,
  tone,
  scale,
  ...props
}: React.ComponentProps<'h4'> & VariantProps<typeof h4Variants>) {
  return <h4 className={cn(h4Variants({ tone, scale }), className)} {...props} />
}

const pVariants = cva('', {
  variants: {
    tone: toneVariants,
    size: {
      inherit: '',
      body: 'text-body',
      lead: 'text-lg font-light',
      hero: 'text-fs-3xl font-light leading-relaxed',
      sm: 'text-sm',
      xs: 'text-fs-md',
    },
  },
  defaultVariants: { tone: 'default', size: 'body' },
})

export function TypographyP({
  className,
  tone,
  size,
  ...props
}: React.ComponentProps<'p'> & VariantProps<typeof pVariants>) {
  return <p className={cn(pVariants({ tone, size }), className)} {...props} />
}

const spanVariants = cva('', {
  variants: {
    tone: toneVariants,
    variant: {
      inherit: '',
      eyebrow: 'font-mono text-fs-sm font-bold uppercase tracking-widest',
      eyebrowLoose: 'text-fs-lg font-bold uppercase tracking-widest',
      stat: 'text-xl font-bold',
      caption: 'mt-1 font-mono text-fs-sm font-bold uppercase tracking-widest',
      label: 'text-fs-sm font-bold uppercase tracking-widest',
      monoBadge: 'font-mono text-fs-md font-bold',
    },
  },
  defaultVariants: { tone: 'inherit', variant: 'inherit' },
})

export function TypographySpan({
  className,
  tone,
  variant,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof spanVariants>) {
  return <span className={cn(spanVariants({ tone, variant }), className)} {...props} />
}
